import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook } from "lucide-react";
import TikTok from "@/components/icons/TikTok";
import { useCookieConsent } from "@/context/CookieConsentContext";

const socialLinks = [
  {
    href: "https://instagram.com/evangelizaciondigitalhuelva",
    icon: Instagram,
    label: "Instagram",
  },
  {
    href: "https://facebook.com/profile.php?id=61579343082913",
    icon: Facebook,
    label: "Facebook",
  },
  {
    href: "https://youtube.com/@EvangelizacionDigitalHuelva",
    icon: Youtube,
    label: "YouTube",
  },
  {
    // Antes era un cuadradito con el texto «TK», porque no existía un icono
    // compartido de TikTok pese a estar el SVG en línea en otros tres sitios.
    href: "https://tiktok.com/@evangelizacion.di",
    icon: TikTok,
    label: "TikTok",
  },
];

const footerLinks = [
  { label: "Quiénes Somos", href: "/quienes-somos" },
  { label: "Formación", href: "/formacion" },
  { label: "Eventos", href: "/eventos" },
  { label: "Recursos", href: "/recursos" },
  { label: "Misionero Digital", href: "/misionero-digital" },
  { label: "Contacto", href: "/contacto" },
];

const legalLinks = [
  { label: "Política de Privacidad", href: "/politica-de-privacidad" },
  { label: "Política de Cookies", href: "/politica-de-cookies" },
];

export default function Footer() {
  const { openPreferences } = useCookieConsent();

  return (
    <footer data-testid="footer" className="bg-[#1d1d1f] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-auto flex items-center justify-center flex-shrink-0">
                <img
                  src="/logos/logo-icono.png"
                  alt="Logo Evangelización Digital"
                  className="h-[64px] md:h-[80px] w-auto object-contain"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </div>
              <div>
                <p className="font-heading font-bold text-base leading-none">
                  Evangelización Digital
                </p>
                <p className="text-xs text-white/60 mt-0.5 uppercase tracking-wider">
                  Diócesis de Huelva
                </p>
              </div>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Llevando a Cristo a las redes, a las pantallas y a los corazones.
              Misión digital al servicio del Evangelio.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white hover:text-black hover:-translate-y-0.5 transition-[background-color,color,transform] duration-fast ease-out-apple focus-ring-light"
                  aria-label={s.label}
                >
                  <s.icon className="w-4 h-4" size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-widest text-[#86868b] mb-4">
              Navegación
            </h4>
            <ul className="grid grid-cols-2 gap-2">
              {footerLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-sm text-[#86868b] hover:text-white transition-colors duration-fast focus-ring-light rounded"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-widest text-[#86868b] mb-4">Diócesis de Huelva</h4>
            <address className="not-italic">
              <p className="text-sm text-[#86868b]">Avda. Manuel Siurot, 31</p>
              <p className="text-sm text-[#86868b]">21004 Huelva</p>
              <a
                href="tel:+34959252100"
                className="text-sm text-[#86868b] hover:text-white transition-colors duration-fast block focus-ring-light rounded"
              >
                Tel. 959 252 100
              </a>
              <a
                href="https://diocesisdehuelva.es"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-sm text-[#86868b] hover:text-white underline underline-offset-4 transition-colors"
              >
                Web del Obispado de Huelva
              </a>
            </address>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#86868b] text-center sm:text-left">
            &copy; {new Date().getFullYear()} Delegación de Evangelización Digital &middot;
            Diócesis de Huelva
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {legalLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="text-xs text-[#86868b] hover:text-white transition-colors duration-fast focus-ring-light rounded"
              >
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={openPreferences}
              data-testid="footer-cookie-settings"
              className="text-xs text-[#86868b] hover:text-white transition-colors duration-fast focus-ring-light rounded"
            >
              Configuración de Cookies
            </button>
            <Link
              to="/admin/login"
              className="text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              Acceso Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
